import { themeI18n } from '../utils/i18n_helper';
import { useState, useEffect, useMemo } from 'react';
import { updateAttributes } from '../utils/cart';
import { ButtonContent } from './ButtonContent';
import { multilingual_url } from '../utils/multilingual-url';
import '../../styles/components/store-location.scss';

const myStoreDataInitial = JSON.parse(localStorage.getItem('bentley_my_store'));

export const StoreLocation = ({store, first, map, infoWindowOpen, infoWindowStore, storeInventory}) => {
  const [isMystore, setIsMystore] = useState(myStoreDataInitial?.id == store.id);

  useEffect(() => {
    document.addEventListener('myStoreUpdated', () => {
      const newStoreData = JSON.parse(localStorage.getItem('bentley_my_store'));

      setIsMystore(newStoreData?.id == store.id);
    });

    return () => {
      document.removeEventListener('myStoreUpdated', () => {
        const newStoreData = JSON.parse(localStorage.getItem('bentley_my_store'));

        setIsMystore(newStoreData?.id == store.id);
      });
    }
  }, []);

  const unsetMyStore = () => {
    localStorage.removeItem('bentley_my_store');
    document.dispatchEvent(new Event('myStoreUpdated'));
    updateAttributes({myStore: null});
  }

  const setMyStore = () => {
    localStorage.setItem('bentley_my_store', JSON.stringify(store));
    document.dispatchEvent(new Event('myStoreUpdated'));
    updateAttributes({myStore: store.store_number});
  }

  const RenderOpenIndicator = () => {
    const currentDay = new Date().getDay();
    const currentDate = new Date();

    // hours are stored in this format "10:00-20:00", here we are mapping the hours to an iterable format, new Date().getDay() returns 0 for sunday, 1 for monday, etc.
    const storeHours = {
      0: {
        open: store.sunday_hours?.split("-")[0].split(":")[0],
        openMinutes: store.sunday_hours?.split("-")[0].split(":")[1],
        close: store.sunday_hours?.split("-")[1].split(":")[0],
        closeMinutes: store.sunday_hours?.split("-")[1].split(":")[1]
      },
      1: {
        open: store.monday_hours?.split("-")[0].split(":")[0],
        openMinutes: store.monday_hours?.split("-")[0].split(":")[1],
        close: store.monday_hours?.split("-")[1].split(":")[0],
        closeMinutes: store.monday_hours?.split("-")[1].split(":")[1]
      },
      2: {
        open: store.tuesday_hours?.split("-")[0].split(":")[0],
        openMinutes: store.tuesday_hours?.split("-")[0].split(":")[1],
        close: store.tuesday_hours?.split("-")[1].split(":")[0],
        closeMinutes: store.tuesday_hours?.split("-")[1].split(":")[1]
      },
      3: {
        open: store.wednesday_hours?.split("-")[0].split(":")[0],
        openMinutes: store.wednesday_hours?.split("-")[0].split(":")[1],
        close: store.wednesday_hours?.split("-")[1].split(":")[0],
        closeMinutes: store.wednesday_hours?.split("-")[1].split(":")[1]
      },
      4: {
        open: store.thursday_hours?.split("-")[0].split(":")[0],
        openMinutes: store.thursday_hours?.split("-")[0].split(":")[1],
        close: store.thursday_hours?.split("-")[1].split(":")[0],
        closeMinutes: store.thursday_hours?.split("-")[1].split(":")[1]
      },
      5: {
        open: store.friday_hours?.split("-")[0].split(":")[0],
        openMinutes: store.friday_hours?.split("-")[0].split(":")[1],
        close: store.friday_hours?.split("-")[1].split(":")[0],
        closeMinutes: store.friday_hours?.split("-")[1].split(":")[1]
      },
      6: {
        open: store.saturday_hours?.split("-")[0].split(":")[0],
        openMinutes: store.saturday_hours?.split("-")[0].split(":")[1],
        close: store.saturday_hours?.split("-")[1].split(":")[0],
        closeMinutes: store.saturday_hours?.split("-")[1].split(":")[1]
      }
    }

    const storeOpeningDate = new Date();
    storeOpeningDate.setHours(storeHours[currentDay].open);
    storeOpeningDate.setMinutes(storeHours[currentDay].openMinutes);
    const storeClosingDate = new Date();
    storeClosingDate.setHours(storeHours[currentDay].close);
    storeClosingDate.setMinutes(storeHours[currentDay].closeMinutes);


    const isOpen = currentDate >= storeOpeningDate && currentDate <= storeClosingDate && storeHours[currentDay].close && storeHours[currentDay].open;

    if (isOpen) {
      return (
        <span className='store-location__open-indicator'>
          <span className='store-location__open-indicator-icon store-location__open-indicator-icon--open'/>
          <span className='store-location__open-indicator-text-blurb'>
            {themeI18n('store_locator.open_until')}
            {renderHour(storeHours[currentDay].close, storeHours[currentDay].closeMinutes)}
          </span>
        </span>
      )
    } else {
      const isOpeningLater = currentDate < storeOpeningDate;

      if (isOpeningLater) {
        return (
          <span className='store-location__open-indicator'>
            <span className='store-location__open-indicator-icon store-location__open-indicator-icon--closed'/>
            <span className='store-location__open-indicator-text-blurb'>
              {themeI18n('store_locator.opens_at')}
              {renderHour(storeHours[currentDay].open, storeHours[currentDay].openMinutes)}
            </span>
          </span>
        )
      } else {
        let nextOpenDay;

        for (let i = currentDay + 1; i <= 6; i++) {
          if (storeHours[i].open) {
            nextOpenDay = i;
            break;
          }
        }

        if (!nextOpenDay) {
          for (let i = 0; i <= currentDay; i++) {
            if (storeHours[i].open) {
              nextOpenDay = i;
              break;
            }
          }
        }

        return (
          <span className='store-location__open-indicator'>
            <span className='store-location__open-indicator-icon store-location__open-indicator-icon--closed'/>
            <span className='store-location__open-indicator-text-blurb'>
              {themeI18n('store_locator.opens_on')}
              {themeI18n(`store_locator.days.${nextOpenDay}`)}
              {themeI18n('store_locator.at')}
              {renderHour(storeHours[nextOpenDay].open, storeHours[nextOpenDay].openMinutes)}
            </span>
          </span>
        )
      }
    }
  }

  const renderHour = (hour, minute) => {
    if (window.i18next.language == 'fr') {
      return getFrenchHour(hour, minute);
    } else {
      return getEnglishHour(hour, minute);
    }
  }

  const getFrenchHour = (hour, minute) => {
    if (minute == "00") {
      return hour + 'h' ;
    } else {
      return hour + "h" + minute;
    }
  }

  const getEnglishHour = (hour, minute) => {
    if (hour >= 13) {
      if (minute == "00") {
        return hour - 12 + " p.m.";
      } else {
        return hour - 12 + ":" + minute + " p.m.";
      }
    } else {
      if (minute == "00") {
        return hour + " a.m.";
      } else {
        return hour + ":" + minute + " a.m.";
      }
    }
  }

  const inventoryStatus = useMemo(() => {
    if (storeInventory) {
      let status = 'in-stock'

      if (storeInventory.quantity <= 0) {
        status = 'sold-out';
      } else if (storeInventory.quantity < 25) {
        status = 'low-stock';
      }

      return status;
    }
  }, [storeInventory]);

  return (
    <div className={infoWindowOpen && infoWindowStore.handle == store.handle ? 'store-location store-location--active': 'store-location'} {...first ? {style: {order: -1}} : null}>
      <div className='store-location__name-distance-wrapper'>
        {map ? (
          <button id={`store-${store.handle}`} className='store-location__name store-location__name--clickable' onClick={()=> {
            map.setCenter({lat: parseFloat(store.latitude), lng: parseFloat(store.longitude)});
            map.setZoom(15);
          }}>{store.name}</button>
        ):(
          <p className='store-location__name' >{store.name}</p>
        )}

        <p className='store-location__distance text-small'>{store.distance ? `${store.distance.toFixed(2)} km` : null}</p>
      </div>
      <p className='store-location__address text-small'>{store.address}</p>

      <div className='store-location__details'>
        <RenderOpenIndicator />

        {/* This link will be implemented later */}
        <a className='store-location__details-link link' href={multilingual_url(`/pages/store/${store.handle}`)}>{themeI18n('store_locator.store_details')}</a>
      </div>

      {storeInventory ? (
        <div className={'store-location__inventory-status inventory-status ' + inventoryStatus}>
          <div className="inventory-status__content">
            <div className="inventory-status__icon"/>

            <span>
              {storeInventory.quantity > 0 ?
              themeI18n('products.inventory.in_stock_at', {quantity: storeInventory.quantity, location: store.name})
              :
              themeI18n('products.inventory.sold_out_at', {location: store.name})
              }
            </span>
          </div>
        </div>
      ): null}

      {isMystore ?
        <button type='button'onClick={unsetMyStore} className='store-location__select-button store-location__select-button--selected button'>
          <ButtonContent text={themeI18n('store_locator.selected')} />
        </button>
      :
        <button type='button' onClick={setMyStore} className='store-location__select-button button'>
          <ButtonContent text={themeI18n('store_locator.select')} />
        </button>
      }
    </div>
  )
}
